/**
 * Shared post-sign-in destination resolution.
 *
 * The landing page ("/") is the only supported sign-in surface. Deep-link
 * gates (team invitations, course invitations) hand their destination across
 * sign-in with `?redirect=<path>` and nothing else — never a token, never a
 * secret. Only same-origin absolute paths are accepted so the parameter can
 * never become an open redirect.
 *
 * Both the sign-in control (`HeaderAuth`) and the landing hero's session
 * discovery use this one helper, so an already-valid session and a fresh
 * sign-in land on the same place instead of racing to `/app`.
 */

export const DEFAULT_POST_AUTH_REDIRECT = '/app';

/** True only for a same-origin absolute path (`/x`), never `//host`. */
export function isSafeRedirectPath(value: string | null | undefined): boolean {
  if (!value) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  // A backslash after the leading slash is treated as a scheme-relative URL
  // by some browsers.
  if (value.startsWith('/\\')) return false;
  return true;
}

/**
 * Resolve the destination to use after a successful sign-in.
 * `search` defaults to the current location's query string.
 */
export function getPostAuthRedirect(search?: string): string {
  try {
    const query = search ?? (typeof window !== 'undefined' ? window.location.search : '');
    const r = new URLSearchParams(query).get('redirect');
    if (isSafeRedirectPath(r)) return r as string;
  } catch {
    /* fall through to the default */
  }
  return DEFAULT_POST_AUTH_REDIRECT;
}

/**
 * Fixed, token-free Course Lab join path. The private invitation code never
 * travels in this path, a query string, or an auth redirect — it stays in the
 * learner's browser tab (see CourseLabJoinPage).
 */
export const COURSE_JOIN_PATH = '/course-lab/join';

export type AuthIntent = 'signup' | 'signin';

/**
 * Which sign-in form a deep-link gate asked the landing page to open
 * (`?auth=signup` | `?auth=signin`). Presentation only — it grants nothing.
 */
export function getAuthIntent(search?: string): AuthIntent | null {
  try {
    const query = search ?? (typeof window !== 'undefined' ? window.location.search : '');
    const v = new URLSearchParams(query).get('auth');
    return v === 'signup' || v === 'signin' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Fixed, token-free workspace invitation path. The private invitation code
 * stays in the tab's sessionStorage (see AcceptInvitePage); legacy
 * `/invite/:token` links are stashed and rewritten to this path.
 */
export const WORKSPACE_JOIN_PATH = '/invite';

/** True when sign-in was opened from a workspace invitation. */
export function isWorkspaceInviteHandoff(search?: string): boolean {
  return getPostAuthRedirect(search) === WORKSPACE_JOIN_PATH;
}

/** True when sign-in was opened from a course invitation. */
export function isCourseJoinHandoff(search?: string): boolean {
  return getPostAuthRedirect(search) === COURSE_JOIN_PATH;
}

/**
 * Where a sign-up confirmation email returns. Ordinary sign-ups keep the
 * existing behaviour (site origin). A course-invitation sign-up returns to the
 * fixed same-origin join path — never with the invitation code.
 */
export function getSignupEmailRedirect(search?: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (isCourseJoinHandoff(search)) return origin + COURSE_JOIN_PATH;
  if (isWorkspaceInviteHandoff(search)) return origin + WORKSPACE_JOIN_PATH;
  return origin;
}
